/**
 * PR-B: connectivity health bar for /settings/pocket.
 *
 * Compact horizontal strip with three traffic-light indicators:
 *   App ↔ Database, App ↔ Cache, App ↔ Sales.
 *
 * Auto-polls every 30 seconds. Click "Refresh" to force a re-probe.
 * Sits above the Tabs so the first thing an operator sees is whether
 * Pocket is healthy across all three connections.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { getPocketHealth } from '@/lib/pocketSettingsApi';
import type {
  PocketHealth,
  PocketHealthCheck,
  PocketHealthStatus,
} from '@/types/pocketSettings';

const POLL_INTERVAL_MS = 30_000;

const STATUS_TONE: Record<PocketHealthStatus, { dot: string; label: string }> = {
  ok: { dot: 'bg-emerald-500', label: 'Healthy' },
  degraded: { dot: 'bg-amber-500', label: 'Degraded' },
  down: { dot: 'bg-red-500', label: 'Down' },
  not_configured: { dot: 'bg-muted-foreground/40', label: 'Not configured' },
};

interface IndicatorProps {
  label: string;
  check: PocketHealthCheck | null;
  loading: boolean;
}

function Indicator({ label, check, loading }: IndicatorProps) {
  if (loading || !check) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5">
        <span className="h-2 w-2 rounded-full bg-muted-foreground/30 animate-pulse" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    );
  }
  const tone = STATUS_TONE[check.status] ?? STATUS_TONE.down;
  // Latency is only meaningful when the check actually completed a round trip.
  // A failed probe still carries an elapsed time — time-to-404, time-to-401 —
  // and printing it beside a red dot reads as a successful fast response.
  const showLatency = check.latency_ms != null && check.status !== 'down';
  const detail = check.last_error;
  const fullTitle = detail
    ? `${label} — ${tone.label}: ${detail}`
    : showLatency
      ? `${label} — ${tone.label} · ${check.latency_ms}ms`
      : `${label} — ${tone.label}`;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5"
      // Keyboard-reachable and announced: the reason for a red dot must not be
      // locked behind a desktop hover, since this page's audience runs it on
      // iPads where native tooltips never fire.
      tabIndex={0}
      role="status"
      aria-label={fullTitle}
      title={fullTitle}
    >
      <span className={cn('h-2 w-2 rounded-full shrink-0', tone.dot)} />
      <span className="text-xs text-foreground font-medium">{label}</span>
      {showLatency ? (
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {check.latency_ms}ms
        </span>
      ) : null}
      {detail ? (
        <span
          className={cn(
            'text-[11px] max-w-[18rem] truncate',
            check.status === 'down' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {detail}
        </span>
      ) : null}
    </div>
  );
}

export function HealthIndicatorsBar() {
  const [health, setHealth] = useState<PocketHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const probe = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const data = await getPocketHealth();
      if (!cancelledRef.current) {
        setHealth(data);
        setError(null);
      }
    } catch (e) {
      if (!cancelledRef.current) {
        // Drop the last successful reading. Keeping it painted three green
        // dots with frozen latencies for the whole duration of a health-endpoint
        // outage — an all-clear that was indistinguishable from a live one.
        setHealth(null);
        setError((e as Error)?.message || 'Probe failed');
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
        if (manual) setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    probe(false);
    const id = setInterval(() => probe(false), POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [probe]);

  const checkedAtLabel = health?.checked_at
    ? new Date(health.checked_at).toLocaleTimeString()
    : null;

  return (
    <div className="border border-border rounded-md bg-card px-2 py-1 flex flex-wrap items-center gap-1">
      <Indicator
        label="App ↔ Database"
        check={health?.checks?.app_db ?? null}
        loading={loading}
      />
      <span className="h-4 w-px bg-border/60" />
      <Indicator
        label="App ↔ Cache"
        check={health?.checks?.app_cache ?? null}
        loading={loading}
      />
      <span className="h-4 w-px bg-border/60" />
      <Indicator
        label="App ↔ Sales"
        check={health?.checks?.app_sales ?? null}
        loading={loading}
      />
      <div className="ml-auto flex items-center gap-2 px-2">
        {error ? (
          <span className="text-[11px] text-destructive">{error}</span>
        ) : checkedAtLabel ? (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {checkedAtLabel}
          </span>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={() => probe(true)}
          disabled={refreshing}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')}
          />
        </Button>
      </div>
    </div>
  );
}
